import { useEffect, useState } from "react";
import { Cable } from "lucide-react";

import MockBadge from "@/components/MockBadge";
import { ops } from "@/lib/api";

const KIND = {
  avinode: "supply",
  operator_direct: "supply",
  empty_leg: "supply",
  stripe: "payment",
  docusign: "esign",
  opensanctions: "compliance",
  argus_wyvern: "compliance",
  opensky: "flight",
  resend: "messaging",
  twilio_sms: "messaging",
  twilio_voice: "messaging",
  clearbit: "enrichment",
};

export default function Adapters() {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    ops.adaptersHealth().then(setRows).catch(() => {});
    const id = setInterval(() => ops.adaptersHealth().then(setRows).catch(() => {}), 6000);
    return () => clearInterval(id);
  }, []);

  return (
    <div data-testid="page-adapters" className="p-6 md:p-10">
      <div className="flex items-end justify-between gap-4 mb-8">
        <div className="min-w-0">
          <div className="pj-label mb-2">· Adapter Health · External services</div>
          <h1 className="font-display text-[40px] leading-[1.02] truncate">Adapters</h1>
        </div>
        <div className="shrink-0">
          <MockBadge />
        </div>
      </div>

      <p className="mono text-[12px] text-[rgb(var(--dim))] mb-6 max-w-3xl">
        Every external integration sits behind an adapter interface with a Mock and a Real-stub implementation,
        switched by env vars. Swap any adapter to <strong className="text-[rgb(var(--fg))]">real</strong> mode by setting
        the matching <code>*_ADAPTER_MODE</code> variable and providing credentials — no code changes.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {rows.map((a) => (
          <div key={a.name} className="pj-card p-4 pj-card-hover overflow-hidden" data-testid={`adapter-card-${a.name}`}>
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <Cable size={14} className="text-[rgb(var(--dim))] shrink-0" />
                <span className="mono text-[13px] text-[rgb(var(--fg))] truncate" title={a.name}>{a.name}</span>
              </div>
              <span
                className="mono uppercase tracking-[0.14em] text-[9px] px-1.5 py-0.5 shrink-0 whitespace-nowrap"
                style={{
                  border: "1px solid " + (a.mode === "mock" ? "rgba(176,116,18,0.30)" : "rgba(32,138,78,0.30)"),
                  color: a.mode === "mock" ? "rgb(176,116,18)" : "rgb(32,138,78)",
                  background: a.mode === "mock" ? "rgba(176,116,18,0.10)" : "rgba(32,138,78,0.10)",
                }}
              >
                {a.mode}
              </span>
            </div>
            <div className="mono text-[10px] text-[rgb(var(--dim))] mt-1 uppercase tracking-[0.14em] truncate">
              {a.kind || KIND[a.name] || "—"}
            </div>
            <div className="mt-4 grid grid-cols-2 gap-2">
              <div>
                <div className="pj-label">Status</div>
                <div className="mono text-[12px] mt-1" style={{ color: a.ok ? "rgb(32,138,78)" : "rgb(196,52,44)" }}>
                  {a.ok ? "OK" : "needs config"}
                </div>
              </div>
              <div>
                <div className="pj-label">Latency</div>
                <div className="mono text-[12px] mt-1 truncate">{a.latency_ms || 0}ms</div>
              </div>
            </div>
            {a.detail && (
              <div className="mono text-[10px] text-[rgb(var(--dim))] mt-3 break-words line-clamp-3" title={a.detail}>{a.detail}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
